import axiosInstance from './axiosCreator';

export const CHAT_DOCUMENT_MAX_BYTES = 20 * 1024 * 1024;
export const CHAT_DOCUMENT_MAX_PAGES = 250;
export const CHAT_DOCUMENT_MIME_TYPES = new Set([
  'application/pdf',
  'application/vnd.ms-powerpoint',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation',
  'application/msword',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'text/plain',
  'text/csv',
]);

export interface ChatDocumentDto {
  id: string;
  fileName: string;
  mimeType: string;
  sizeBytes: number;
  status: 'pending' | 'processing' | 'ready' | 'error';
  pageCount: number | null;
  errorMessage: string | null;
  preparationWarning: string | null;
}

export const createChatDocumentUpload = async (file: File) => {
  const response = await axiosInstance.post<{
    document: ChatDocumentDto;
    uploadUrl: string;
  }>('/api/v5/chat/documents/upload-url', {
    fileName: file.name,
    mimeType: file.type,
    sizeBytes: file.size,
  });

  return response.data;
};

export const uploadChatDocument = (
  url: string,
  file: File,
  mimeType: string,
  signal?: AbortSignal
) =>
  fetch(url, {
    method: 'PUT',
    headers: { 'content-type': mimeType },
    body: file,
    signal,
  }).then((response) => {
    if (!response.ok) throw new Error('Upload failed');
  });

export const prepareChatDocument = async (id: string) => {
  const response = await axiosInstance.post<{ document: ChatDocumentDto }>(
    `/api/v5/chat/documents/${id}/prepare`,
    undefined,
    { timeout: 150_000 }
  );

  return response.data.document;
};

export const getChatDocument = async (id: string) => {
  const response = await axiosInstance.get<{ document: ChatDocumentDto }>(
    `/api/v5/chat/documents/${id}`
  );

  return response.data.document;
};

/** Fetch the authenticated viewer-ready PDF bytes for a chat attachment. */
export const fetchChatDocumentViewerFile = async (
  id: string
): Promise<ArrayBuffer> => {
  const response = await axiosInstance.get(
    `/api/v5/chat/documents/${id}/viewer/file`,
    { responseType: 'arraybuffer' }
  );

  return response.data as ArrayBuffer;
};

export const deleteChatDocument = (id: string) =>
  axiosInstance.delete(`/api/v5/chat/documents/${id}`);

export const openChatDocument = async (id: string) => {
  // Opening the tab is part of the click gesture. Waiting for the owner-signed
  // URL first makes browsers treat it as an unsolicited popup.
  const popup = window.open('', '_blank');
  if (!popup) throw new Error('Your browser blocked the document tab.');
  popup.opener = null;

  try {
    const response = await axiosInstance.get<{
      url: string;
      mimeType: string;
      fileName: string;
    }>(`/api/v5/chat/documents/${id}/view`);

    popup.location.replace(response.data.url);
  } catch (error) {
    popup.close();
    throw error;
  }
};

export const isAcceptedChatDocument = (file: File) =>
  CHAT_DOCUMENT_MIME_TYPES.has(file.type) ||
  /\.(pdf|ppt|pptx|doc|docx|txt|csv)$/i.test(file.name);
